import { useEffect, useState } from "react";
import { Steps, message } from "antd";
import Header from "../HomePage/header";
import Footer from "../HomePage/footer";
import StepPersonalDetails from "./StepPersonalDetails";
import NomineeDetails from "./NomineeDetils";
import BankDetails from "./BankDatils";
import InvestmentOptions from "./InvestmentOptions";
import PaymentPranNoGeneration from "./PaymentPranNoGeneration";
import StepKYC from "./StepKYC";
import { GetRegistrationDetails } from "@/utils/types/NPS/Stepper/InitialProcessRegistration";
import InitialProcessRegistrationService from "@/services/NPS/Stepper/APIs/InitialProcessRegistrationService";
import GetRegistrationDetailsService from "@/services/NPS/Stepper/APIs/getRegistrationDetailsService";
import Loader from "@/projects/NPS/Stepper/common/Loader";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";

const NewSubscriberRegistration: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [kycForm, setKycForm] = useState<GetRegistrationDetails | null>(null);
  const [initialDetails, setInitialDetails] = useState<any | null>(null);
  const [isNominationStep, setIsNominationStep] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nextStep, setNextStep] = useState<string | null>(null);
  const [isBankStep, setIsBankStep] = useState<boolean>(false);
  const [isInvestmentStep, setIsInvestmentStep] = useState(false);
  const [isPaymentStep, setIsPaymentStep] = useState(false);
  const [isPaymentSuccessModalClose, setIsPaymentSuccessModalClose] =
    useState(false);
  const [registrationDetails, setRegistrationDetails] =
    useState<Registration | null>(null);

  const steps = [
    { title: "KYC" },
    { title: "Personal Details" },
    { title: "Nominee Details" },
    { title: "Bank Details" },
    { title: "Investment Choice" },
    { title: "Payment" },
  ];

  useEffect(() => {
    if (current === 0) {
      // Trigger API call when KYC step is reached
      const fetchRegistrationDetails = async () => {
        try {
          const response =
            await InitialProcessRegistrationService.getRegistrationDetails();
          if (response.success) {
            setKycForm(response?.enquiry);
          } else {
            console.error("Failed to fetch registration details.");
          }
        } catch (error) {
          console.error("Error fetching registration details:", error);
          console.error("Error fetching registration details.");
        }
      };

      fetchRegistrationDetails();
    }
  }, [current, setKycForm]); // Fetch details when KYC step is reached

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [current]);

  useEffect(() => {
    const fetchRegistrationDetails = async () => {
      setIsLoading(true); // Set loading to true when API call starts
      try {
        const response =
          await GetRegistrationDetailsService.getRegistrationDetails();

        if (response.success) {
          const { registration } = response;
          setRegistrationDetails(registration); // Save registration details

          if (registration?.next_step === "nomination" && !isBankStep) {
            setNextStep(registration?.next_step);
            setInitialDetails(registration?.initialDetails);
            setCurrent(2); // Set the current step to "Nominee Details"
          } else if (registration?.next_step === "bank" && !isBankStep) {
            setNextStep(registration?.next_step);
            setInitialDetails(registration?.initialDetails);
            setCurrent(3); // Set the current step to "Bank Details"
          } else if (registration?.next_step === "scheme" && !isBankStep) {
            setIsInvestmentStep(true);
            setNextStep(registration?.next_step);
            setInitialDetails(registration?.initialDetails);
            setCurrent(4); // Set the current step to "Investment Options"
          } else if (registration?.next_step === "payment" && !isBankStep) {
            setIsPaymentStep(true);
            setNextStep(registration?.next_step);
            setInitialDetails(registration?.initialDetails);
            setCurrent(5); // Set the current step to "Payment"
          }
        } else {
          console.error("Failed to fetch registration details.");
        }
      } catch (error) {
        console.error("Error fetching registration details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch registration details if nextStep is undefined
    fetchRegistrationDetails();
  }, [current, isBankStep, isPaymentSuccessModalClose]); // Dependency is now only on `nextStep` and `backFromStep`

  const next = () => setCurrent(current + 1);
  const prev = () => {
    setCurrent(current - 1);
    setIsBankStep(true);
    setNextStep(null);
    setIsPaymentStep(true);
    setIsInvestmentStep(true);
    setIsNominationStep(true);
  };

  return (
    <>
      <Header />
      <div className="container mx-auto px-4 py-12 lg:px-10 xl:px-10 2xl:px-0">
        <h1 className="mb-5 pl-5 text-center font-nunito text-xl font-semibold text-[#266985] md:mb-12 md:text-left md:text-[34px]">
          New Subscriber Registration
        </h1>

        {/* Stepper */}
        <div className="-mx-4 overflow-x-auto md:mx-0">
          <div className="stepper-mobile-wrapper px-4 md:px-0">
            <Steps
              current={current}
              responsive
              labelPlacement="vertical"
              className="[&_.ant-steps-item-container]:rounded-lg [&_.ant-steps-item-container]:border [&_.ant-steps-item-container]:border-solid [&_.ant-steps-item-container]:border-[#DFE7EC] [&_.ant-steps-item-container]:p-3 [&_.ant-steps-item-container]:md:rounded-none [&_.ant-steps-item-container]:md:border-none [&_.ant-steps-item-container]:md:p-0 [&_.ant-steps-item-finish>.ant-steps-item-container>.ant-steps-item-tail::after]:bg-[#258996] [&_.ant-steps-item-process>.ant-steps-item-container>.ant-steps-item-tail::after]:bg-[#DFE7EC] [&_.ant-steps-item-tail::after]:!left-0 [&_.ant-steps-item-tail::after]:!right-0 [&_.ant-steps-item-tail::after]:!top-1/2 [&_.ant-steps-item-tail::after]:!translate-y-[-50%] [&_.ant-steps-item-tail]:top-[24px] [&_.ant-steps-item-tail]:hidden [&_.ant-steps-item-tail]:md:block"
              items={steps.map((item, index) => ({
                key: item.title,
                title: (
                  <span
                    className={`text-center font-nunito text-[14px] font-bold md:text-[18px] ${
                      index === current
                        ? "text-[#000]" // Active step title → black
                        : "text-[#707070]" // Inactive/completed steps → gray
                    }`}
                  >
                    {item.title}
                  </span>
                ),
                icon: (
                  <div
                    className={`flex items-center justify-center rounded-full border-2 border-solid font-nunito font-semibold md:border-0 ${
                      index < current
                        ? "border-transparent bg-gradient-to-l from-[#1AAE90] to-[#258996] text-white md:border-0"
                        : index === current
                          ? "border-[#258996] bg-[#E4FFFB] text-[#266985] md:border-0"
                          : "border-[#DFE7EC] bg-[#f5f5f5] text-[#707070] md:border-0"
                    } h-[40px] w-[40px] flex-shrink-0 md:h-[50px] md:w-[50px]`}
                  >
                    {index < current ? "✓" : index + 1}
                  </div>
                ),
              }))}
            />
          </div>
        </div>
        <style>{`
          .stepper-mobile-wrapper .ant-steps {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
            .ant-steps.ant-steps-vertical >.ant-steps-item:not(:last-child)>.ant-steps-item-container>.ant-steps-item-tail {
    display: none;
}
    .ant-steps.ant-steps-vertical >.ant-steps-item .ant-steps-item-title {
    line-height: 20px;
}

.stepper-mobile-wrapper .ant-steps-item-title {
  white-space: nowrap !important;
}
   
@media (max-width: 768px){
        .ant-steps-item-container {
    display: flex;
    align-items: center;
    padding: 10px !important;

    }
    .ant-steps-item-content {
    display: flex !important;
    align-items: center;
    }
    }

          @media (min-width: 768px) {
            .stepper-mobile-wrapper .ant-steps {
              display: flex !important;
              gap: 0;
              width: 100%;
              justify-content: space-between;
            }
       
          }
          .stepper-mobile-wrapper .ant-steps-item {
            flex: 1;
            min-width: 0;
            width: 100%;
          }
          @media (min-width: 768px) {
            .stepper-mobile-wrapper .ant-steps-item {
              flex: 1;
              min-width: 0;
              width: auto;
            }
          }
            @media (max-width: 768px) {
            .stepper-mobile-wrapper .ant-steps-item-title {
                white-space: nowrap !important;
            font-size: 12px !important;
            text-align: center !important;
            }
          }
          .stepper-mobile-wrapper .ant-steps-item-container {
            width: 100% !important;
          }
          @media (min-width: 768px) {
            .stepper-mobile-wrapper .ant-steps-item-container {
              width: 100% !important;
            }
          }
          @media (min-width: 768px) {
            .stepper-mobile-wrapper .ant-steps-item-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 100%;
            }
          }
        `}</style>

        {isLoading && <Loader message="Fetching registration details..." />}

        {/* Step Components */}
        {current === 0 && (
          <StepKYC
            key={registrationDetails ? "loaded" : "loading"}
            kycForm={kycForm}
            setKycForm={setKycForm}
            onNext={next}
            setCurrent={setCurrent}
            message={message}
            registrationDetails={registrationDetails}
          />
        )}
        {current === 1 && (
          <StepPersonalDetails
            key={registrationDetails ? "loaded" : "loading"}
            kycForm={kycForm}
            onNext={next}
            // onPrev={prev}
            setNextStep={setNextStep}
            setIsNominationStep={setIsNominationStep}
            setInitialDetails={setInitialDetails}
            registrationDetails={registrationDetails}
          />
        )}
        {current === 2 && (isNominationStep || nextStep === "nomination") && (
          <NomineeDetails
            key={registrationDetails ? "loaded" : "loading"}
            ackId={initialDetails?.ackId}
            nextStep={nextStep ?? ""}
            initialDetails={initialDetails}
            onNext={next}
            onPrev={prev}
            setNextStep={setNextStep}
            setInitialDetails={setInitialDetails}
            registrationDetails={registrationDetails}
          />
        )}
        {current === 3 && (isBankStep || nextStep === "bank") && (
          <BankDetails
            key={registrationDetails ? "loaded" : "loading"}
            ackId={initialDetails?.ackId}
            nextStep={nextStep ?? ""}
            initialDetails={initialDetails}
            onNext={next}
            onPrev={prev}
            setNextStep={setNextStep}
            setInitialDetails={setInitialDetails}
            registrationDetails={registrationDetails}
          />
        )}
        {current === 4 && (isInvestmentStep || nextStep === "scheme") && (
          <InvestmentOptions
            key={registrationDetails ? "loaded" : "loading"}
            ackId={initialDetails?.ackId}
            nextStep={nextStep ?? ""}
            initialDetails={initialDetails}
            onNext={next}
            onPrev={prev}
            setNextStep={setNextStep}
            setInitialDetails={setInitialDetails}
            registrationDetails={registrationDetails}
          />
        )}
        {current === 5 && (isPaymentStep || nextStep === "payment") && (
          <PaymentPranNoGeneration
            key={registrationDetails ? "loaded" : "loading"}
            ackId={initialDetails?.ackId}
            nextStep={nextStep ?? ""}
            initialDetails={initialDetails}
            onNext={next}
            onPrev={prev}
            setNextStep={setNextStep}
            setInitialDetails={setInitialDetails}
            registrationDetails={registrationDetails}
            isPaymentSuccessModalClose={isPaymentSuccessModalClose}
            setIsPaymentSuccessModalClose={setIsPaymentSuccessModalClose}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default NewSubscriberRegistration;
